import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableMaintenanceStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportMaintenanceStation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchMaintenanceReportStation,
  updateReportFilters,
  setReportFiltersData,
  fetchListFilters,
  setMaintenanceStationReportUpdate,
  setSortData,
  refreshMaintenanceReportStation,
} from '../../../../redux/slices/ReportMaintenanceStationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
}

/**
 * Reports management page
 * @param {any} properties - The properties
 */
function MaintenanceReportTableStation(properties: ReportTableProperties) {
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter } = properties;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isMaintenanceStationReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportMaintenanceStation);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  /**
   * Fetches Maintenance report data for the selected facility.
   * @param {any} data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchMaintenanceReportStation({
          ...data,
          accountCustomId: reportsData?.accountId,
        }),
      );
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchMaintenanceReportStation({ ...queryParameters, page, limit, sortField, sortBy });
  };
  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: reports,
      disableEdit: true,
      disableDelete: true,
    });

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableMaintenanceStationReport,
      /**
       * S.No view
       * @param {any} report - The report data
       * @param {number} index - The index
       */
      customData: (report: AvailableMaintenanceStationReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableMaintenanceStationReport,
      /**
       * S.No view
       * @param {any} report - The report data
       */
      customData: (report: AvailableMaintenanceStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableMaintenanceStationReport,
      sortAllow: true,
    },
    {
      label: t('inspection'),
      accessorKey: 'inspectionCount' as keyof AvailableMaintenanceStationReport,
      sortAllow: true,
    },
    {
      label: t('repair'),
      accessorKey: 'repairCount' as keyof AvailableMaintenanceStationReport,
      sortAllow: true,
    },

    {
      label: t('softwareUpdate'),
      accessorKey: 'softwareUpdateCount' as keyof AvailableMaintenanceStationReport,
      sortAllow: true,
    },
    {
      label: t('complete'),
      accessorKey: 'maintenanceCompletedCount' as keyof AvailableMaintenanceStationReport,
      sortAllow: true,
    },
    {
      label: t('inProgress'),
      accessorKey: 'maintenanceInProgressCount' as keyof AvailableMaintenanceStationReport,
      sortAllow: true,
    },
    {
      label: t('postponed'),
      accessorKey: 'maintenancePostponedCount' as keyof AvailableMaintenanceStationReport,
      sortAllow: true,
    },
  ];

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page,
      limit,
    });
  };

  /**
   * Current columns for Maintenance Station Report export
   */
  const currentColumns = [
    { label: 'SNo', accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('inspection'), accessorKey: 'inspectionCount' },
    { label: t('repair'), accessorKey: 'repairCount' },
    { label: t('softwareUpdate'), accessorKey: 'softwareUpdateCount' },
    { label: t('complete'), accessorKey: 'maintenanceCompletedCount' },
    { label: t('inProgress'), accessorKey: 'maintenanceInProgressCount' },
    { label: t('postponed'), accessorKey: 'maintenancePostponedCount' },
    { label: 'Created', accessorKey: 'createdAt' },
    { label: 'Last Updated', accessorKey: 'updatedAt' },
  ];

  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchMaintenanceStationListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailableMaintenanceStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'deviceCount':
                return row?.deviceCount ?? '0';

              case 'inspectionCount':
                return row?.inspectionCount ?? '0';

              case 'repairCount':
                return row?.repairCount ?? '0';

              case 'softwareUpdateCount':
                return row?.softwareUpdateCount ?? '0';

              case 'maintenanceCompletedCount':
                return row?.maintenanceCompletedCount ?? '0';

              case 'maintenanceInProgressCount':
                return row?.maintenanceInProgressCount ?? '0';

              case 'maintenancePostponedCount':
                return row?.maintenancePostponedCount ?? '0';

              case 'createdAt':
                return (row as any).createdAt ? ConvertUTCtoDateTime((row as any).createdAt) : '-';

              case 'updatedAt':
                return (row as any).updatedAt ? ConvertUTCtoDateTime((row as any).updatedAt) : '-';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Handle the auto refresh function
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshMaintenanceReportStation({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        accountCustomId: reportsData?.accountId,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {any} value - The request parameters
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortBy,
      sortField,
    });
  };
  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isMaintenanceStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setMaintenanceStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isMaintenanceStationReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'maintenanceReport' &&
      filters.length > 1 &&
      reportsData.reportByType === 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          disablePagination={false}
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={handleApiRequest}
          version="v2"
          height={370}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          disableHeader={false}
          filters={filters}
          created
          lastUpdated
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          leftTopContent={leftTopContent}
          refershCurrentPages={ReferenceCurrentPages}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          sizeChangeButtonRequired
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          onHiddenColumnsChange={setHiddenColumns}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        />
      </div>
    </div>
  );
}

export default MaintenanceReportTableStation;
